import type { ReactNode } from 'react'
import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/core'
import type { RootOptions } from 'react-dom/client'
import { redactProviderKeys } from '@/shared/lib/redactProviderKeys'
import { sanitizeBreadcrumb, sanitizeSentryEvent } from '@/shared/lib/sentrySanitize'
import { tryOneTimeChunkReload } from '@/shared/monitoring/chunkLoadRecovery'
import {
  classifySentryError,
  SENTRY_IGNORE_ERRORS,
} from '@/shared/monitoring/sentryErrorTaxonomy'
import {
  isSentryReplayEnabled,
  parseOptionalSampleRate,
  resolveSentryEnvironment,
  resolveSentryRelease,
} from '@/shared/monitoring/sentry.helpers'

/**
 * Sentry com fallback quando o módulo falha ao carregar (ex.: Supabase local
 * inacessível corrompendo resolução do Vite). Evita tela em branco na tela de login.
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined

const noop = () => {}
const NoopBoundary = ({ children }: { children: ReactNode }) => children

const createNoopSentry = () => ({
  init: noop,
  captureException: noop,
  captureMessage: noop,
  addBreadcrumb: noop,
  setUser: noop,
  ErrorBoundary: NoopBoundary,
  browserTracingIntegration: () => ({}),
  replayIntegration: () => ({}),
})

let SentryInstance: Awaited<typeof import('@sentry/react')> | ReturnType<typeof createNoopSentry> =
  createNoopSentry()

let sentryClientReady = false

/**
 * Equivale ao `sendDefaultPii: false` do SDK 10 (removido no 11, onde o padrão passou a coletar tudo).
 * Sem IP nem `user.*` automáticos, sem cookies e sem headers HTTP.
 */
export const SENTRY_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
} as const

const applySentryClassification = (event: ErrorEvent, message: string, type?: string): ErrorEvent | null => {
  const classification = classifySentryError(message, type)
  if (classification.action === 'drop') return null

  event.tags = { ...event.tags, 'error.category': classification.category }
  event.fingerprint = classification.fingerprint

  if (classification.category === 'chunk_load') {
    const release = resolveSentryRelease() ?? __APP_VERSION__
    tryOneTimeChunkReload(release)
  }

  return event
}

export const initSentry = async (): Promise<void> => {
  const dsn = SENTRY_DSN?.trim()
  if (!dsn) {
    sentryClientReady = false
    return
  }

  try {
    const mod = await import('@sentry/react')
    SentryInstance = mod

    const tracesSampleRate = parseOptionalSampleRate(
      import.meta.env.VITE_SENTRY_TRACE_SAMPLE_RATE as string | undefined,
      0.1,
    )
    const replayEnabled = isSentryReplayEnabled()

    mod.init({
      dsn,
      release: resolveSentryRelease() ?? __APP_VERSION__,
      environment: resolveSentryEnvironment(),
      /** App clínico: sem IP/usuário/cookies/headers automáticos; PII reduzida em sanitizeSentryEvent. */
      dataCollection: SENTRY_DATA_COLLECTION,

      tracesSampleRate,
      replaysSessionSampleRate: replayEnabled ? 0.05 : 0,
      /** Replay só em erro por defeito; máscaras reduzem risco de capturar dados de pacientes na UI. */
      replaysOnErrorSampleRate: replayEnabled ? 1.0 : 1.0,

      ignoreErrors: SENTRY_IGNORE_ERRORS,
      denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i],

      beforeBreadcrumb(breadcrumb: Breadcrumb) {
        if (breadcrumb.category === 'fetch' && breadcrumb.data && typeof breadcrumb.data.url === 'string') {
          const u = breadcrumb.data.url
          if (/api\.allorigins\.win/i.test(u) || /corsproxy\.io/i.test(u)) {
            return null
          }
        }
        return sanitizeBreadcrumb(breadcrumb)
      },

      beforeSend(event: ErrorEvent, _hint: EventHint) {
        void _hint
        if (event.message) event.message = redactProviderKeys(event.message)
        for (const ex of event.exception?.values ?? []) {
          if (ex.value) ex.value = redactProviderKeys(ex.value)
        }
        for (const crumb of event.breadcrumbs ?? []) {
          if (crumb.message) crumb.message = redactProviderKeys(crumb.message)
        }
        const firstEx = event.exception?.values?.[0]
        const message = firstEx?.value ?? event.message ?? ''
        const type = firstEx?.type

        const classified = applySentryClassification(event, message, type)
        if (!classified) return null

        return sanitizeSentryEvent(classified) as ErrorEvent
      },

      integrations: [
        mod.browserTracingIntegration(),
        mod.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
    })
    sentryClientReady = true
  } catch {
    SentryInstance = createNoopSentry()
    sentryClientReady = false
  }
}

/**
 * Handlers do React 19 (`createRoot` options) para erros que o boundary não vê
 * (ex.: fluxo default de `onCaughtError` só faz console.error).
 * Chamar só após `initSentry()` resolver.
 */
export function getSentryReactRootOptions(): RootOptions | undefined {
  if (!sentryClientReady) return undefined
  const mod = SentryInstance as typeof import('@sentry/react')
  if (typeof mod.reactErrorHandler !== 'function') return undefined
  const onError = mod.reactErrorHandler()
  return {
    onUncaughtError: onError,
    onCaughtError: onError,
  }
}

export const Sentry = new Proxy({} as typeof import('@sentry/react'), {
  get(_, prop) {
    return (SentryInstance as Record<string, unknown>)[prop as string]
  },
})
